/**
 * ChangePasswordModal
 *
 * Two things about the backend shape this component:
 *
 * 1. A successful change revokes *every* session for the user, including the one
 *    that made the request. So there is no "carry on where you left off" path:
 *    the only correct follow-up is to sign out and send the user back to the
 *    login screen. We say so explicitly rather than letting the next API call
 *    fail with a mystery 401.
 *
 * 2. The password policy lives on the server and comes back as a 400. We render
 *    that message as-is instead of duplicating the rules here, where they would
 *    drift.
 *
 * When `required` is true (the account is flagged `must_change_password`) the
 * dialog cannot be dismissed: no cancel button, no Escape, no overlay click.
 */

import { useState } from 'react'
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react'
import * as api from '../api'
import { useAuth } from '../context/AuthContext'
import Modal from './Modal'

export default function ChangePasswordModal({ isOpen, onClose, required = false }) {
  const { signOut } = useAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  const reset = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
    setDone(false)
    setSubmitting(false)
  }

  const handleClose = () => {
    if (required || submitting) return
    reset()
    onClose?.()
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting || done) return

    setError(null)

    if (!currentPassword || !newPassword) {
      setError('Fill in your current and new password.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('The new password and confirmation do not match.')
      return
    }
    if (newPassword === currentPassword) {
      setError('The new password must be different from your current one.')
      return
    }

    setSubmitting(true)
    try {
      await api.changePassword(currentPassword, newPassword)
      // Every session is now dead, including ours.
      setDone(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSubmitting(false)
    } catch (err) {
      // 400 carries the server's policy text; anything else carries its own.
      setError(err?.message || 'Could not change your password.')
      setSubmitting(false)
    }
  }

  const footer = done ? (
    <button type="button" className="btn btn-primary" onClick={() => signOut()}>
      Return to sign in
    </button>
  ) : (
    <>
      {!required && (
        <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </button>
      )}
      <button
        type="submit"
        form="change-password-form"
        className="btn btn-primary"
        disabled={submitting}
        aria-busy={submitting}
      >
        {submitting ? (
          <>
            <Loader2 size={15} className="spin" aria-hidden="true" />
            Updating…
          </>
        ) : (
          <>
            <ShieldCheck size={15} aria-hidden="true" />
            Change password
          </>
        )}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={required ? 'Set a new password' : 'Change password'}
      dismissable={!required && !done}
      maxWidth="460px"
      footer={footer}
    >
      {done ? (
        <div role="status" className="auth-notice auth-notice-ok">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>
            Your password was changed. All active sessions were signed out for security, so you
            need to sign in again with your new password.
          </span>
        </div>
      ) : (
        <form id="change-password-form" onSubmit={handleSubmit} noValidate>
          {required && (
            <p className="auth-notice auth-notice-warn">
              <AlertTriangle size={15} aria-hidden="true" />
              <span>Your account requires a new password before you can continue.</span>
            </p>
          )}

          {error && (
            <div role="alert" className="login-alert">
              <AlertTriangle size={15} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="cp-current">Current password</label>
            <input
              id="cp-current"
              type="password"
              className="form-control"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cp-new">New password</label>
            <input
              id="cp-new"
              type="password"
              className="form-control"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cp-confirm">Confirm new password</label>
            <input
              id="cp-confirm"
              type="password"
              className="form-control"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
          </div>

          <p className="text-xs text-muted">
            Changing your password signs out every active session, including this one.
          </p>
        </form>
      )}
    </Modal>
  )
}
